import {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useApolloClient } from '@apollo/client';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';
import { ITax, SettingsContext } from 'settings';
import { EPaymentItemType, IWorker, TPaymentItem, TShopItem } from 'types';
import { getItemTotalPriceForReceipt } from 'utils';

import { GET_LICENSE, IGetLicenseResponse } from './schema';
import { CashboxService } from './service';
import {
  EReceiptType,
  ICashboxContext,
  IOFDState,
  ISaleItem,
  TCreatePaymentFn,
  TRefundPaymentFn,
} from './types';
import { useIsCashboxEnabled } from './useIsCashboxEnabled';

const DEFAULT_OFD_STATE: IOFDState = { count: 0 };

const INITIAL_CONTEXT: ICashboxContext = {
  validateHonestSign: () =>
    Promise.resolve({
      validate: true,
    }),
  loadKKMs: () => Promise.reject(),
  openWorkShift: () => Promise.reject(),
  closeWorkShift: () => Promise.reject(),
  createPayment: () => Promise.reject(),
  refundPayment: () => Promise.reject(),
  printXReport: () => Promise.reject(),
  OFDState: DEFAULT_OFD_STATE,
};

export const formatFIO = ({
  last_name,
  first_name,
}: Partial<IWorker>): string =>
  `${last_name} ${first_name && first_name[0] + '.'}`;

export const formatTitle = (type: EPaymentItemType, title?: string): string => {
  switch (type) {
    case EPaymentItemType.DEPOSIT:
      return 'Пополнение депозита';
    case EPaymentItemType.BONUS:
      return 'Пополнение бонусов';
    case EPaymentItemType.GOOD:
      return title ?? 'Без названия';
    case EPaymentItemType.SERVICE:
      return `Услуга "${title}"`;
    case EPaymentItemType.TARIFF:
      return `Тариф "${title}"`;
  }
};

const getRefundItemTaxes = (
  item: TPaymentItem,
  taxes: Array<ITax>
): Omit<ITax, 'entities'> => {
  const settingsTax = taxes.find((tax) => tax.entities?.includes(item.type));

  if (
    item.type === EPaymentItemType.TARIFF ||
    item.type === EPaymentItemType.DEPOSIT
  ) {
    return {
      nds: settingsTax?.nds ?? null,
      taxVariant: settingsTax?.taxVariant ?? null,
    };
  } else if (item.type === EPaymentItemType.BONUS) {
    return {
      nds: null,
      taxVariant: null,
    };
  }

  return {
    nds: item.entity.vat ?? settingsTax?.nds ?? null,
    taxVariant: item.entity?.tax_system ?? settingsTax?.taxVariant ?? null,
  };
};

const getShopItemTaxes = (
  item: TShopItem,
  taxes: Array<ITax>
): Omit<ITax, 'entities'> => {
  const settingsTax = taxes.find((tax) => tax.entities?.includes(item.type));

  if (item.type === EPaymentItemType.TARIFF) {
    return {
      nds: settingsTax?.nds ?? null,
      taxVariant: settingsTax?.taxVariant ?? null,
    };
  }

  return {
    nds: item.original?.vat ?? settingsTax?.nds ?? null,
    taxVariant: item.original?.tax_system ?? settingsTax?.taxVariant ?? null,
  };
};

export const CashboxContext = createContext<ICashboxContext>(INITIAL_CONTEXT);

export const Cashbox: FC = ({ children }) => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { me },
  } = useContext(AuthContext);
  const {
    state: {
      settings: { selectedCashboxINN, taxes, depositRefillTitle, cashierName },
    },
  } = useContext(SettingsContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const [OFDState, setOFDState] = useState<IOFDState>(DEFAULT_OFD_STATE);

  const isCashboxEnabled = useIsCashboxEnabled();

  const apolloClient = useApolloClient();

  useEffect(() => {
    if (!isCashboxEnabled || !permissions?.has('payment_create')) {
      cashboxService.setLicense(null);
      return;
    }

    apolloClient
      .query<IGetLicenseResponse>({
        query: GET_LICENSE,
      })
      .then((data) => {
        cashboxService.setLicense(data.data.cashBoxLicenseToken ?? null);
      });
  }, [isCashboxEnabled, permissions]);

  const cashboxService = useMemo(() => new CashboxService(), []);

  useEffect(() => {
    cashboxService.setTranslator(t);
  }, [t, cashboxService]);

  useEffect(() => {
    cashboxService.setCashboxINN(selectedCashboxINN);
  }, [selectedCashboxINN]);
  useEffect(() => {
    cashboxService.setOperator(
      cashierName ? cashierName : me ? formatFIO(me) : null
    );
  }, [me?.first_name, me?.last_name, cashierName]);

  useEffect(() => {
    if (!isCashboxEnabled || !permissions?.has('payment_create')) return;

    const intervalId = setInterval(
      () =>
        cashboxService.getState().then(({ Info }) => {
          setOFDState({
            count: Info.OFD_NumErrorDoc,
            firstErrorDate: Info.OFD_DateErrorDoc,
          });
        }),
      600000
    );

    return () => {
      clearInterval(intervalId);
      setOFDState({ ...DEFAULT_OFD_STATE });
    };
  }, [isCashboxEnabled, permissions]);

  const createPayment = useCallback<TCreatePaymentFn>(
    ({
      method,
      items,
      discount,
      availableItems,
      depositSum,
      cardSum,
      postPaymentSum,
    }) => {
      if (depositSum) {
        const depositTax = taxes.find((tax) =>
          tax.entities?.includes(EPaymentItemType.DEPOSIT)
        );

        return cashboxService.printCheck({
          type: EReceiptType.SALE,
          paymentMethod: method,
          totalPrice: depositSum,
          cardSum,
          items: [
            {
              type: EPaymentItemType.DEPOSIT,
              amount: 1,
              price: depositSum,
              totalPrice: depositSum,
              title:
                depositRefillTitle ?? formatTitle(EPaymentItemType.DEPOSIT),
              NDS: depositTax?.nds,
            },
          ],
          taxVariant: depositTax?.taxVariant,
        });
      }

      if (postPaymentSum) {
        const settingsTax = taxes.find((tax) =>
          tax.entities?.includes(EPaymentItemType.TARIFF)
        );

        return cashboxService.printCheck({
          type: EReceiptType.SALE,
          paymentMethod: method,
          totalPrice: postPaymentSum,
          cardSum,
          items: [
            {
              type: EPaymentItemType.TARIFF,
              amount: 1,
              price: postPaymentSum,
              totalPrice: postPaymentSum,
              title: 'Поминутный',
              NDS: settingsTax?.nds,
            },
          ],
          taxVariant: settingsTax?.taxVariant,
        });
      }

      if (!items) {
        return Promise.reject('Items for payment was not provided');
      }

      const itemsForKKM: Array<ISaleItem> = [];

      for (const item of items) {
        const itemTotalPrice = getItemTotalPriceForReceipt(
          item,
          discount,
          availableItems
        );

        itemsForKKM.push({
          title: formatTitle(item.type, item.original.title),
          amount: item.amount,
          price: item.cost,
          totalPrice: itemTotalPrice,
          type: item.type,
          NDS: getShopItemTaxes(item, taxes).nds,
          sign: item.type === EPaymentItemType.GOOD ? item.sign : undefined,
          is_excise:
            item.type === EPaymentItemType.GOOD && item.original.is_excise,
        });
      }

      const totalPrice = itemsForKKM.reduce((sum, it) => {
        return sum + (it?.totalPrice ?? 0);
      }, 0);

      return cashboxService.printCheck({
        type: EReceiptType.SALE,
        taxVariant: getShopItemTaxes(items[0], taxes).taxVariant,
        paymentMethod: method,
        cardSum,
        totalPrice,
        items: itemsForKKM,
      });
    },
    [cashboxService, taxes]
  );

  const refundPayment = useCallback<TRefundPaymentFn>(
    ({ items, sum, method, card_sum, cash_sum }) =>
      cashboxService.printCheck({
        type: EReceiptType.REFUND,
        paymentMethod: method,
        totalPrice: sum,
        cardSum: card_sum,
        cashSum: cash_sum,
        taxVariant: getRefundItemTaxes(items[0], taxes).taxVariant,
        items: items.map((item) => ({
          type: item.type,
          title: formatTitle(
            item.type,
            'entity' in item ? item.entity?.title : undefined
          ),
          amount: item.type === 'DEPOSIT' ? 1 : item.amount,
          price:
            item.type === 'DEPOSIT'
              ? item.sum
              : Number((item.sum / item.amount).toFixed(2)),
          totalPrice: item.sum,
          NDS: getRefundItemTaxes(item, taxes).nds,
        })),
      }),
    [cashboxService, taxes]
  );

  return (
    <CashboxContext.Provider
      value={{
        loadKKMs: cashboxService.loadKKMs,
        openWorkShift: cashboxService.openWorkShift,
        validateHonestSign: cashboxService.validateHonestSign,
        createPayment,
        refundPayment,
        closeWorkShift: cashboxService.closeWorkShift,
        printXReport: cashboxService.printXReport,
        OFDState,
      }}
    >
      {children}
    </CashboxContext.Provider>
  );
};
